import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { StatusCodes } from 'http-status-codes';
import { sha256 } from 'js-sha256';

import { ApiEndpoints } from 'constants/endpointsEnum';

import { apiResponseSchema } from 'schemas/api/common';
import { updatedUserDetailsSchema, userDetailsListSchema } from 'schemas/api/user';

import { getBaseSalesURL } from 'services/api';
import axios, { createAxiosError, errorCatchOnHandler } from 'services/axios';

import type { TUserDetails } from 'types/api/user';

/**
 * Sends the request `buildRequest` returns and resolves with what `read` takes from the body of an OK answer; any
 * failure is reported and rejected. The request is built inside the error handling so a failure to build it is
 * reported like any other failure.
 */
const request = async <TResult>(
    tag: string,
    buildRequest: () => AxiosRequestConfig,
    read: (body: unknown) => TResult
): Promise<TResult> => {
    try {
        const requestCfg = buildRequest();
        const response = (await axios.request(requestCfg)) as AxiosResponse<unknown> | undefined;
        if (response?.status === StatusCodes.OK) return read(response.data);
        return createAxiosError(requestCfg, response);
    } catch (error) {
        errorCatchOnHandler({ error, tag, fnc: 'apiUserServices' });
        return Promise.reject(error);
    }
};

/** The signed-in user's own account: the profile details and the password. */
export const apiUserServices = {
    /** The profile details, or `null` when the API has none for the user. */
    getUserDetails: async (userId: number, signal?: AbortSignal) =>
        request(
            'getUserDetails',
            () => ({ url: `${getBaseSalesURL()}${ApiEndpoints.USER_DETAILS}/${userId}`, method: 'GET', signal }),
            (body) => userDetailsListSchema.parse(apiResponseSchema.parse(body).data)
        ),

    /** Saves the profile details and resolves with the new session token, when the API sends one. */
    updateUserDetails: async (userId: number, payload: TUserDetails) =>
        request(
            'updateUserDetails',
            () => ({ url: `${getBaseSalesURL()}${ApiEndpoints.USER_DETAILS}/${userId}`, method: 'PUT', data: payload }),
            (body) => updatedUserDetailsSchema.parse(apiResponseSchema.parse(body).data)
        ),

    /** Changes the password, which travels as its SHA-256 hash. */
    updatePassword: async (userId: number, password: string) =>
        request(
            'updatePassword',
            () => ({ url: `${getBaseSalesURL()}${ApiEndpoints.USER}/${userId}`, method: 'PUT', data: { password_: sha256(password) } }),
            () => undefined
        ),
};
